import React, { useState, useEffect, useCallback } from 'react';
import { Wrench, MessageSquare, Camera, Trash2, Loader2, Send } from 'lucide-react';
import { solicitudesSeguimientoService, UPLOADS_BASE_URL } from '../../../../services/api';
import useModal from '../../../../hooks/useModal';

const fmtFechaHora = (f) => new Date(f).toLocaleString('es-VE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
});

const TIPO_CONFIG = {
  avance: { label: 'Avance', icon: Wrench, className: 'bg-blue-100 text-blue-700' },
  comentario: { label: 'Comentario', icon: MessageSquare, className: 'bg-slate-100 text-slate-700' }
};

const SeguimientoOT = ({ solicitudId }) => {
  const { confirm, ModalRenderer } = useModal();
  const [entradas, setEntradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tipo, setTipo] = useState('avance');
  const [descripcion, setDescripcion] = useState('');
  const [fotos, setFotos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await solicitudesSeguimientoService.getBySolicitud(solicitudId);
      setEntradas(res.data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar el seguimiento');
    } finally {
      setLoading(false);
    }
  }, [solicitudId]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleFotosChange = (e) => {
    const archivos = Array.from(e.target.files || []).slice(0, 5);
    setFotos(archivos);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!descripcion.trim()) return;
    setGuardando(true);
    setError('');
    try {
      await solicitudesSeguimientoService.create(solicitudId, { tipo, descripcion, fotos });
      setDescripcion('');
      setFotos([]);
      document.getElementById(`seguimiento-fotos-${solicitudId}`).value = '';
      await cargar();
    } catch (err) {
      setError(err.message || 'Error al registrar');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    const ok = await confirm('Eliminar registro', '¿Eliminar este registro y sus fotos?');
    if (!ok) return;
    setEliminandoId(id);
    try {
      await solicitudesSeguimientoService.delete(id);
      await cargar();
    } catch (err) {
      setError(err.message || 'Error al eliminar');
    } finally {
      setEliminandoId(null);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex gap-2">
          {Object.entries(TIPO_CONFIG).map(([valor, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button
                key={valor}
                type="button"
                onClick={() => setTipo(valor)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  tipo === valor ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                }`}
              >
                <Icon size={14} /> {cfg.label}
              </button>
            );
          })}
        </div>

        <textarea
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          rows={2}
          placeholder={tipo === 'avance' ? 'Describe el avance del trabajo...' : 'Escribe un comentario...'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
          required
        />

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <Camera size={16} className="text-gray-400" />
            <input
              id={`seguimiento-fotos-${solicitudId}`}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleFotosChange}
              className="text-xs"
            />
          </label>
          {fotos.length > 0 && <span className="text-xs text-gray-500">{fotos.length} foto(s) seleccionada(s)</span>}
          <button
            type="submit"
            disabled={guardando || !descripcion.trim()}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
          >
            {guardando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Agregar
          </button>
        </div>
      </form>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

      {loading ? (
        <div className="text-center py-6 text-gray-400 text-sm">Cargando...</div>
      ) : entradas.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm italic">Sin avances ni comentarios registrados todavía.</div>
      ) : (
        <div className="space-y-3">
          {entradas.map((entrada) => {
            const cfg = TIPO_CONFIG[entrada.tipo] || TIPO_CONFIG.comentario;
            const Icon = cfg.icon;
            return (
              <div key={entrada.id} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${cfg.className}`}>
                      <Icon size={12} /> {cfg.label}
                    </span>
                    <span className="text-xs text-gray-400">{fmtFechaHora(entrada.created_at)}</span>
                    {entrada.usuario_nombre && <span className="text-xs text-gray-500">· {entrada.usuario_nombre}</span>}
                  </div>
                  <button
                    onClick={() => handleEliminar(entrada.id)}
                    disabled={eliminandoId === entrada.id}
                    className="text-gray-400 hover:text-red-600 p-1"
                    title="Eliminar"
                  >
                    {eliminandoId === entrada.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
                <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap">{entrada.descripcion}</p>
                {entrada.fotos && entrada.fotos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {entrada.fotos.map((foto) => (
                      <a
                        key={foto.id}
                        href={`${UPLOADS_BASE_URL}/uploads/seguimiento/${foto.ruta_archivo}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={`${UPLOADS_BASE_URL}/uploads/seguimiento/${foto.ruta_archivo}`}
                          alt={foto.nombre_archivo}
                          className="w-20 h-20 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {ModalRenderer}
    </div>
  );
};

export default SeguimientoOT;
